import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Switch,
  Card,
  Spin,
  Space,
  Row,
  Col,
  Typography,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PhoneOutlined,
  UserOutlined,
  LinkOutlined,
  CopyOutlined,
  ReloadOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import { deliveryPersonsApi } from '../../utils/api';

const { Title } = Typography;

const DeliveryPersonForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isNewPerson = id === 'new';

  // État du formulaire
  const [loading, setLoading] = useState(!isNewPerson);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Données du formulaire - uniquement les champs essentiels
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    is_active: true
  });

  // Token d'accès public du livreur
  const [publicAccessToken, setPublicAccessToken] = useState(null);

  // Chargement initial des données du livreur si mode édition
  useEffect(() => {
    if (!isNewPerson) {
      loadDeliveryPerson();
    }
  }, [id]);

  /**
   * Charge les données d'un livreur existant depuis l'API
   * Récupère uniquement les champs essentiels : nom, téléphone, statut et token d'accès
   */
  const loadDeliveryPerson = async () => {
    try {
      setLoading(true);
      const response = await deliveryPersonsApi.getById(id);
      const data = response.data;

      // Mise à jour des données du formulaire avec les champs essentiels uniquement
      const formValues = {
        name: data.name || '',
        phone: data.phone || '',
        is_active: data.is_active !== undefined ? data.is_active : true
      };

      setFormData(formValues);
      form.setFieldsValue(formValues);

      // Récupérer le token d'accès public
      if (data.public_access_token) {
        setPublicAccessToken(data.public_access_token);
      }

    } catch (error) {
      console.error('Erreur lors du chargement du livreur:', error);
      message.error('Erreur lors du chargement du livreur');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gère la soumission du formulaire
   * Crée ou met à jour un livreur avec les informations de base
   * @param {Object} values - Les valeurs du formulaire (nom, téléphone, statut)
   */
  const handleSubmit = async (values) => {
    setSaving(true);

    try {
      if (isNewPerson) {
        // Création d'un nouveau livreur
        const response = await deliveryPersonsApi.create(values);
        message.success('Livreur créé avec succès');
        navigate('/app/delivery-persons');
      } else {
        // Mise à jour d'un livreur existant
        await deliveryPersonsApi.update(id, values);
        message.success('Livreur mis à jour avec succès');
        navigate('/app/delivery-persons');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Copie le lien d'accès du livreur dans le presse-papiers
   */
  const handleCopyLink = () => {
    if (!publicAccessToken) return;

    const link = `${window.location.origin}/delivery/my-orders/${publicAccessToken}`;
    navigator.clipboard.writeText(link).then(() => {
      message.success('Lien copié dans le presse-papiers !');
    }).catch(() => {
      message.error('Erreur lors de la copie du lien');
    });
  };

  /**
   * Régénère le token d'accès public du livreur
   */
  const handleRegenerateToken = async () => {
    if (!id || isNewPerson) return;

    // Confirmation de l'utilisateur
    if (!window.confirm('Êtes-vous sûr de vouloir régénérer le lien d\'accès ? L\'ancien lien ne fonctionnera plus.')) {
      return;
    }

    setRegenerating(true);

    try {
      const response = await deliveryPersonsApi.regenerateToken(id);
      setPublicAccessToken(response.data.delivery_person.public_access_token);
      message.success('Nouveau lien généré avec succès !');
    } catch (error) {
      console.error('Erreur lors de la régénération du token:', error);
      message.error('Erreur lors de la régénération du lien');
    } finally {
      setRegenerating(false);
    }
  };

  /**
   * Ouvre WhatsApp pour partager le lien avec le livreur
   */
  const handleShareWhatsApp = () => {
    if (!publicAccessToken || !formData.phone) return;

    const link = `${window.location.origin}/delivery/my-orders/${publicAccessToken}`;
    const text = `Bonjour ${formData.name},\n\nVoici votre lien personnel pour consulter vos commandes de livraison :\n\n${link}\n\nEnregistrez ce lien dans vos favoris pour y accéder facilement.`;
    const whatsappUrl = `https://wa.me/${formData.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(text)}`;

    window.open(whatsappUrl, '_blank');
  };

  // Affichage d'un loader pendant le chargement des données
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px' }}>
      {/* En-tête avec bouton retour et titre */}
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            type="link" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/app/delivery-persons')}
          />
          <Title level={2}>
            {isNewPerson ? 'Nouveau Livreur' : `Modifier ${formData.name}`}
          </Title>
        </Space>
      </div>

      {/* Formulaire simplifié */}
      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={formData}
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            {/* Champ Nom - obligatoire */}
            <Col span={24}>
              <Form.Item
                name="name"
                label="Nom"
                rules={[{ required: true, message: 'Le nom est obligatoire' }]}
              >
                <Input 
                  prefix={<UserOutlined />} 
                  placeholder="Nom complet du livreur"
                  size="large"
                />
              </Form.Item>
            </Col>

            {/* Champ Téléphone - obligatoire */}
            <Col span={24}>
              <Form.Item
                name="phone"
                label="Téléphone"
                rules={[{ required: true, message: 'Le téléphone est obligatoire' }]}
              >
                <Input 
                  prefix={<PhoneOutlined />} 
                  placeholder="Numéro de téléphone"
                  size="large"
                />
              </Form.Item>
            </Col>

            {/* Switch Actif/Inactif */}
            <Col span={24}>
              <Form.Item
                name="is_active"
                label="Statut du livreur"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Actif"
                  unCheckedChildren="Inactif"
                />
              </Form.Item>
            </Col>

            {/* Bouton de soumission */}
            <Col span={24} style={{ textAlign: 'right', marginTop: 16 }}>
              <Space>
                <Button
                  onClick={() => navigate('/app/delivery-persons')}
                >
                  Annuler
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                  size="large"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Card pour afficher le lien d'accès du livreur - uniquement en mode édition */}
      {!isNewPerson && publicAccessToken && (
        <Card
          style={{ marginTop: 24 }}
          title={
            <Space>
              <LinkOutlined />
              <span>Lien d'accès pour le livreur</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Description */}
            <div style={{
              // Encadré informatif de la charte privée ; le lien sécurisé reste inchangé.
              background: 'var(--private-soft, #e6f7ff)',
              border: '1px solid var(--private-border, #91d5ff)',
              borderRadius: '4px',
              padding: '12px',
              color: 'var(--private-ink, #0050b3)'
            }}>
              <p style={{ margin: 0, fontSize: '14px' }}>
                <strong>Lien personnel et sécurisé</strong> - Partagez ce lien avec {formData.name} pour qu'il puisse consulter ses commandes en cours sans authentification.
              </p>
            </div>

            {/* Champ avec le lien */}
            <Form.Item label="URL d'accès" style={{ marginBottom: 0 }}>
              <Input
                readOnly
                value={`${window.location.origin}/delivery/my-orders/${publicAccessToken}`}
                size="large"
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
            </Form.Item>

            {/* Boutons d'action */}
            <Space wrap>
              <Button
                type="primary"
                icon={<CopyOutlined />}
                onClick={handleCopyLink}
                size="large"
              >
                Copier le lien
              </Button>
              <Button
                icon={<WhatsAppOutlined />}
                onClick={handleShareWhatsApp}
                size="large"
                style={{ backgroundColor: '#25D366', color: 'white', borderColor: '#25D366' }}
              >
                Envoyer par WhatsApp
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRegenerateToken}
                loading={regenerating}
                danger
                size="large"
              >
                Régénérer le lien
              </Button>
            </Space>

            {/* Avertissement pour la régénération */}
            <div style={{
              background: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '4px',
              padding: '10px',
              fontSize: '13px',
              color: '#ad6800'
            }}>
              ⚠️ <strong>Attention:</strong> Si vous régénérez le lien, l'ancien lien ne fonctionnera plus. Le livreur devra utiliser le nouveau lien.
            </div>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default DeliveryPersonForm;
